import type { ErrorHandler, RawLocation, Route, RouteMeta } from 'vue-router/types/router';

import Vue from 'vue';
import VueRouter, { isNavigationFailure, NavigationFailureType } from 'vue-router';

import routes from './routes';

import { useGlobalAppState } from '@/store/app-state';
import { useUserBilling } from '@/store/user-billing';
import { useProfileData } from '@/store/user-data';

// vue-router не даёт нам почему-то передавать query через route guard, делаем это
class CustomVueRouter extends VueRouter {
  // @ts-expect-error monkey patching
  push(to: RawLocation, onComplete?: (route: Route) => void, onAbort?: ErrorHandler): void {
    try {
      super.push(to, onComplete, onAbort);
    }
    catch (exception) {
      if (import.meta.env.DEV === false) {
        if (isNavigationFailure(exception, NavigationFailureType.duplicated))
          return;
      }

      throw exception;
    }
  }

  async replace(to: RawLocation): Promise<Route> {
    return super.replace(to).catch((exception) => {
      if (import.meta.env.DEV === false) {
        if (isNavigationFailure(exception, NavigationFailureType.duplicated))
          return {} as Route;
      }

      throw exception;
    });
  }
}

if (window.location.search.length) {
  // не совладал с тем как vue-router управляет query - храним utm в другом месте
  const initialSearchParams = new URLSearchParams(window.location.search);
  for (const [key, value] of initialSearchParams) {
    if (key.startsWith('utm_') || key === 'ref') {
      localStorage.setItem(key, value);
      document.cookie = [
        `${key}=${encodeURIComponent(value)}`,
        'Secure',
        import.meta.env.PROD && `Domain=${import.meta.env.VITE_APP_COOKIE_DOMAIN}`,
        `Expires=${new Date(Date.now() + (2_678_400 * 1000)).toUTCString()}`,
        `Max-Age=${2_678_400}`,
        'SameSite=lax',
      ]
        .filter((item) => Boolean(item))
        .join('; ');
    }
  }
}

const router = new CustomVueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    const position = savedPosition ?? { x: 0, y: 0 };
    if (to.path === from.path || to?.meta?.noScroll)
      return;
    return new Promise((resolve) => {
      // ждём полного завершения анимации на странице (если имеется), и делаем скролл
      setTimeout(() => resolve(position), 500);
    });
  },
});

router.afterEach((to) => {
  if (to.meta?.pageTitle !== undefined) {
    // null - сброс на обычное наименование (из dotenv), без упоминания страниц
    Vue.changeTitle(to.meta.pageTitle);
  }
});

router.beforeEach(async (to, from, next) => {
  if (to.path === from.path || to.params.bypassBeforeEach === 'true') // при смене query params или hash ничего делать не нужно, игнорируем все сценарии ниже (или если явно указано миновать проверки)
    return next();

  const computedMeta = to.matched.reduce((acc, route) => ({ ...acc, ...route.meta }), {}) as RouteMeta;

  const globalAppState = useGlobalAppState();
  const { hasDeviceId, isQuickAccessOn, hasPinSet, isPinNeverInstalled, isAuthenticated, role } = storeToRefs(globalAppState);
  const { update, isLoading } = useProfileData({ init: false });
  const { update: updateBilling, isLoading: isBillingLoading } = useUserBilling({ init: false });

  if (!isLoading && (to.path.includes('cabinet') || to.name === 'index'))
    await update();

  if (role.value === 'undefined' && to.name !== 'pickRole')
    globalAppState.pickInitialRole();
  if (role.value !== 'undefined' && (to.name === 'pickRole' || to.path.includes('account')))
    globalAppState.switchToUndefinedUser();

  if (!isBillingLoading && isAuthenticated.value && (to.path.includes('cabinet') || to.name === 'index'))
    updateBilling();

  if (computedMeta.authRequired && !isAuthenticated.value)
    return next({ name: 'quickAccess' });

  if (to.name !== 'setCode' && hasDeviceId.value && ((isQuickAccessOn.value && !hasPinSet.value) || isPinNeverInstalled.value))
    return next({ name: 'setCode' });

  if (computedMeta.role && (role.value !== computedMeta.role))
    return next({ name: 'pickRole' });

  return next();
});

export default router;
export { CustomVueRouter };
